// src/components/audio/InlineAudioCell.js
//
// Tiny audio player for table cells (Data Preview, Cleaning Studio, etc.).
//
// DESIGN INTENT
// Cells are small. A full AudioPlayer (even compact mode) is too tall for a
// 40px row. This component renders just three things:
//   1. A 22px circular play/pause button (gradient brand cyan)
//   2. The filename, truncated
//   3. The duration, once known
//
// Click the play button: starts playing inline (the audio element lives in
// this row and pauses everyone else's). Click again to pause.
//
// When users want the full review experience (scrubbing, speed control,
// keyboard shortcuts, waveform) they navigate to the Audio Audit workspace.
// This is intentional — keep cells uncluttered, keep Audio Audit powerful.
//
// LIFECYCLE
// On click → fetch bytes via Tauri (cached at module level so revisits are
// instant) → wrap in blob URL → assign to a hidden <audio> element → play.
// A single global "current playing cell" coordinates so only one InlineAudioCell
// plays at a time (via a small event bus).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { resolveAudioPath } from './AudioPlayer';
import { getAudioFormat } from './audioFormats';

// ── Module-level keyframes injection ──────────────────────────────────────
// Inject the spinner @keyframes ONCE per page load, not on every render.
// Per-render <style> tags multiply with table size (each flagged row mounts
// an InlineAudioCell, so a 50-row table would emit 50 identical <style>
// nodes). Production React batches these differently than dev, which
// cascades into layout thrashing — visible as a "squeezed" modal that
// computes its dimensions before the keyframes settle. One global tag
// avoids the problem entirely.
if (typeof document !== 'undefined' && !document.getElementById('inline-audio-cell-keyframes')) {
  const tag = document.createElement('style');
  tag.id = 'inline-audio-cell-keyframes';
  tag.textContent = '@keyframes inline-aud-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(tag);
}

// ── Module-level coordination so only ONE inline cell plays at a time ─────
// When a new cell starts playing, all other cells receive a pause signal.
const _cellListeners = new Set();
const _emitPlayStart = (id) => {
  for (const listener of _cellListeners) listener(id);
};

// Module-level cache: { absPath -> { url: blobUrl, refs: number, mime: string } }
// We refcount so a path used by two cells (rare) doesn't get prematurely revoked.
const _byteCache = new Map();
const _acquireBlobUrl = async (path, tauriApi, filename) => {
  if (_byteCache.has(path)) {
    const entry = _byteCache.get(path);
    entry.refs++;
    return entry.url;
  }
  const bytes = await tauriApi.invoke('read_file_bytes', { path });
  let view;
  if (bytes instanceof Uint8Array) view = bytes;
  else if (bytes instanceof ArrayBuffer) view = new Uint8Array(bytes);
  else if (Array.isArray(bytes)) view = new Uint8Array(bytes);
  else throw new Error('Unexpected byte format');
  const fmt = getAudioFormat(filename || path);
  const blob = new Blob([view], { type: fmt?.mime || 'audio/mpeg' });
  const url = URL.createObjectURL(blob);
  _byteCache.set(path, { url, refs: 1, mime: blob.type });
  return url;
};
const _releaseBlobUrl = (path) => {
  const entry = _byteCache.get(path);
  if (!entry) return;
  entry.refs--;
  if (entry.refs <= 0) {
    URL.revokeObjectURL(entry.url);
    _byteCache.delete(path);
  }
};

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

let _cellIdSeq = 0;

/**
 * InlineAudioCell — minimal player for embedding in table cells.
 *
 * Props:
 *   filename:   string   the cell value (relative path / filename)
 *   mediaDir:   string   the project's media directory (absolute)
 *   tauriApi:   { invoke }
 *   isDarkMode: bool
 *   onOpenAudit: () => void   optional: called when user clicks the filename
 *                              (e.g., navigate to Audio Audit workspace)
 *   compact:    bool     if true, render only the play button (no filename,
 *                        no duration, no progress bar). Used in dense tables
 *                        like the Sentinel/Probe Depth/Gated flagged-rows
 *                        tables where the surrounding cell already provides
 *                        the variable/text context.
 *   compactTitle: string optional tooltip override for compact mode (defaults
 *                        to "Play <filename>")
 */
const InlineAudioCell = ({
  filename, mediaDir, tauriApi, isDarkMode, onOpenAudit,
  compact = false, compactTitle,
}) => {
  const [cellId] = useState(() => ++_cellIdSeq);
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  const [duration, setDuration] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const blobUrlRef = useRef(null);
  const acquiredPathRef = useRef(null);

  // Tokens — minimal, just what this tiny widget needs
  const tokens = isDarkMode
    ? {
        text:      '#E7EAF0',
        textMuted: '#6B7180',
        border:    'rgba(255,255,255,0.08)',
        accent:    '#38B6FF',
        accentDeep:'#1B6BE0',
        error:     '#EF4444',
      }
    : {
        text:      '#15181D',
        textMuted: '#6B7180',
        border:    'rgba(0,0,0,0.08)',
        accent:    '#1B6BE0',
        accentDeep:'#1453B0',
        error:     '#DC2626',
      };

  const absPath = filename && mediaDir ? resolveAudioPath(mediaDir, filename) : null;

  // Listen for "another cell started playing" events so we pause ourselves
  useEffect(() => {
    const onAnyCellPlay = (otherId) => {
      if (otherId !== cellId && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
    _cellListeners.add(onAnyCellPlay);
    return () => _cellListeners.delete(onAnyCellPlay);
  }, [cellId]);

  // Cleanup blob URL refcount on unmount
  useEffect(() => {
    return () => {
      if (acquiredPathRef.current) {
        _releaseBlobUrl(acquiredPathRef.current);
        acquiredPathRef.current = null;
      }
    };
  }, []);

  const handlePlayPause = useCallback(async (e) => {
    e.stopPropagation();
    if (!absPath || !tauriApi?.invoke) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }

    // Need to load if we haven't yet
    if (!audio.src) {
      setLoading(true);
      setError(null);
      try {
        const url = await _acquireBlobUrl(absPath, tauriApi, filename);
        acquiredPathRef.current = absPath;
        blobUrlRef.current = url;
        audio.src = url;
        audio.load();
      } catch (err) {
        const msg = String(err?.message || err);
        setError(/not found|os error 2/i.test(msg) ? 'File not found' : 'Load failed');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Tell other cells to pause
    _emitPlayStart(cellId);
    audio.play().then(() => setPlaying(true)).catch((err) => {
      setError(`Play failed: ${err.message}`);
    });
  }, [absPath, tauriApi, filename, cellId]);

  // Audio element handlers
  const onLoadedMeta = () => {
    if (audioRef.current) {
      const d = audioRef.current.duration;
      setDuration(Number.isFinite(d) ? d : null);
    }
  };
  const onTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };
  const onEnded = () => {
    setPlaying(false);
    setCurrentTime(0);
  };

  // Click filename (not the play button) → navigate to Audio Audit
  const handleFilenameClick = (e) => {
    if (onOpenAudit) {
      e.stopPropagation();
      onOpenAudit();
    }
  };

  // Render the filename as just-the-basename to avoid showing the full path
  const displayName = (() => {
    if (!filename) return '';
    const m = String(filename).match(/[^\\/]+$/);
    return m ? m[0] : String(filename);
  })();

  if (!filename) return null;
  if (!mediaDir) {
    // Compact mode: silently hide. No play possible without media_dir, and the
    // surrounding cell already provides the context.
    if (compact) return null;
    // Default mode: show plain filename (as Data Preview always did)
    return <span style={{ color: tokens.textMuted, fontSize: '12px', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{displayName}</span>;
  }

  const playProgressPct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: compact ? '6px' : '8px',
      maxWidth: '100%',
    }}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onLoadedMetadata={onLoadedMeta}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        preload="none"
      />

      {/* Play button */}
      <button
        onClick={handlePlayPause}
        disabled={loading || !!error}
        title={isPlaying ? 'Pause' : (error || compactTitle || 'Play')}
        style={{
          width: '22px', height: '22px',
          borderRadius: '50%',
          background: error
            ? 'rgba(239,68,68,0.15)'
            : `linear-gradient(135deg, ${tokens.accentDeep}, ${tokens.accent})`,
          border: 'none',
          color: error ? tokens.error : '#FFFFFF',
          cursor: loading || error ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          padding: 0,
          boxShadow: error ? 'none' : `0 1px 4px ${tokens.accent}55`,
          transition: 'transform 0.1s ease',
        }}
        onMouseEnter={(e) => { if (!loading && !error) e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {loading
          ? <Loader2 size={11} style={{ animation: 'inline-aud-spin 0.9s linear infinite' }} />
          : error
            ? <AlertCircle size={11} />
            : (isPlaying ? <Pause size={10} fill="white" /> : <Play size={10} fill="white" style={{ marginLeft: '1px' }} />)
        }
      </button>

      {/* Filename — click to open Audio Audit (hidden in compact mode) */}
      {!compact && (
        <span
          onClick={handleFilenameClick}
          style={{
            fontSize: '12px',
            color: tokens.text,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            minWidth: 0, flex: '0 1 auto',
            cursor: onOpenAudit ? 'pointer' : 'default',
            textDecoration: onOpenAudit && isPlaying ? 'underline dotted' : 'none',
            textUnderlineOffset: '3px',
          }}
          title={`${filename}${onOpenAudit ? ' — click to open in Audio Audit' : ''}`}
        >
          {displayName}
        </span>
      )}

      {/* Duration / progress (hidden in compact mode) */}
      {!compact && (duration || isPlaying) && (
        <span style={{
          fontSize: '10.5px',
          color: tokens.textMuted,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}>
          {isPlaying ? `${formatTime(currentTime)} / ${formatTime(duration)}` : formatTime(duration)}
        </span>
      )}

      {/* Tiny progress bar — only visible when playing (hidden in compact mode) */}
      {!compact && isPlaying && duration > 0 && (
        <div style={{
          width: '40px', height: '3px',
          background: tokens.border,
          borderRadius: '2px',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <div style={{
            width: `${playProgressPct}%`, height: '100%',
            background: `linear-gradient(90deg, ${tokens.accentDeep}, ${tokens.accent})`,
            transition: 'width 100ms linear',
          }} />
        </div>
      )}

      {/* Compact mode mini-progress: a tiny 24px bar to the right of the
          button, only when playing — gives reviewer a sense of "still playing"
          without consuming visual space */}
      {compact && isPlaying && duration > 0 && (
        <div style={{
          width: '24px', height: '2px',
          background: tokens.border,
          borderRadius: '2px',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <div style={{
            width: `${playProgressPct}%`, height: '100%',
            background: `linear-gradient(90deg, ${tokens.accentDeep}, ${tokens.accent})`,
            transition: 'width 100ms linear',
          }} />
        </div>
      )}
    </div>
  );
};

export default InlineAudioCell;